import { ActivatedRoute, NavigationEnd, Router, UrlTree } from '@angular/router';
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TagCommanderService } from 'ngx-tag-commander';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { v4 as uuid } from 'uuid';

import {
  BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, Subscription,
  catchError, distinctUntilChanged, filter, first, map, switchMap, take, takeUntil, tap
} from 'rxjs';

import { UserAccountFacade } from '@spartacus/user/account/root';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { CurrentProductService } from '@spartacus/storefront';
import { Cart } from '@spartacus/cart/base/root';

import {
  BaseSiteService, Category, CmsService, CurrencyService, LanguageService, OccEndpointsService, Page, Product, ScriptPlacement
} from '@spartacus/core';

import { BrandsEnum } from '@seb/web-design-system/core';

import '../models/augmented-spartacus-cart.model';

import {
  AppEnvironment, Bundle, CartGroupEntry, CustomCategory, CustomOrder, CustomOrderEntry, DataLayerCart,
  DataLayerProductOrBundle, DataLayerSearch, DataLayerTransaction, Profil, TMSScript
} from '../models';

import {
  PdpTypeEnum, ProductTypeEnum, StockAvailabilityEnum, StockStatusEnum, StoreMarketEnum,
  TmsScriptExecutionTypeEnum, TmsScriptModeEnum, TMSScriptPositionEnum
} from '../enums';

import {
  areCurrentPageEqual, areQueryEqual, areSegmentsEqual, areTargetsEqual, findBundleEntries,
  findStandaloneEntries, getEntryDiscountForProduct, getPdpType, getProductEntryDiscount,
  getProductVoucherCode, getProductVoucherDiscount, getVoucherCode, getVoucherDiscount,
  getVoucherOperation
} from '../utils';

import { OrderFacade } from '../facades';

import { ExtendedWarrantyService } from './extended-warranty.service';
import { CustomScriptLoader } from './custom-script-loader.service';
import { ConfigurationService } from './configuration.service';
import { CustomAuthService } from './custom-auth.service';
import { BaseStoreService } from './base-store.service';
import { HobStateService } from './hob-state.service';
import { CategoryService } from './category.service';
import { BundleService } from './bundle.service';

import { WebCommonConfig } from '../config/configs/web-common.config';
import { APP_ENVIRONMENT } from '../constants/environment-token';
import { HobMapper } from '../mappers/hob.mapper';

type ProductList = {
  id: string;
  products: Product[];
  pushCount: number;
};

const TARGET_LIST = ['product', 'accessoryProduct', 'ifu'];

@Injectable({
  providedIn: 'root'
})
export class DatalayerService implements OnDestroy {
  private readonly destroy$: Subject<void> = new Subject();
  private tmsScriptModeEnum = TmsScriptModeEnum;
  private readonly productList$: Subject<ProductList> = new BehaviorSubject(undefined);
  private navigation: NavigationEnd;
  private disableDatalayer: boolean;

  private readonly searchData$ = new BehaviorSubject<{
    id: string;
    searchQuery: string;
    searchType: string;
    searchTotal: number;
  }>(undefined);

  private lastSubjectId: string;
  private tcContainers: { code: string; executionType: TmsScriptExecutionTypeEnum }[] = [];
  private datalayerSubscription: Subscription;
  private isScriptLoaded$ = new BehaviorSubject<boolean>(false);

  public get isDisabled() { return this.disableDatalayer; }

  constructor(
    private tagCommanderService: TagCommanderService,
    private configService: ConfigurationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private occEndpointsService: OccEndpointsService,
    private http: HttpClient,
    private scriptLoader: CustomScriptLoader,
    private baseSiteService: BaseSiteService,
    private cmsService: CmsService,
    private readonly hobMapper: HobMapper,
    private languageService: LanguageService,
    private currencyService: CurrencyService,
    private activeCartService: ActiveCartService,
    private authService: CustomAuthService,
    private userAccountFacade: UserAccountFacade,
    private categoryService: CategoryService,
    private bundleService: BundleService,
    private currentProductService: CurrentProductService,
    private deviceDetectorService: DeviceDetectorService,
    private orderService: OrderFacade,
    private translateService: TranslateService,
    private baseStoreService: BaseStoreService,
    private extendedWarrantyService: ExtendedWarrantyService,
    private readonly hobStateService: HobStateService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_ENVIRONMENT) appEnvironment: AppEnvironment
  ) {
    this.disableDatalayer = !!appEnvironment.disableTagCommander;

    if (this.disableDatalayer) {
      console.info('DATALAYER is DISABLED !');
    }
  }

  private static getDatalayerCategory(category: Category): Record<string, any> {
    let dataLayerCategory;
    if (category) {
      const isSis = category.url.includes('/csc/');
      const mainCategory = category['subCategories']
        ? category.name
        : decodeURIComponent(category.url.split('/')[isSis ? 3 : 1])
          ?.split('-')
          ?.join(' ');
      const subCategory = category['subCategories'] ? '' : category.name;
      dataLayerCategory = {
        marketing_category: isSis ? '' : mainCategory,
        marketing_subcategory: isSis ? '' : subCategory,
        SIS_category: isSis ? mainCategory : '',
        SIS_subcategory: isSis ? subCategory : '',
        category_id: category.code,
        category_product_type: isSis ? 'accessory' : 'products',
        product_brand: category?.['brandCorner'] ? category?.['brandCorner'] : BrandsEnum.default
      };
    } else {
      dataLayerCategory = {
        category_id: '',
        category_product_type: '',
        marketing_category: '',
        marketing_subcategory: ''
      };
    }
    return dataLayerCategory;
  }

  private static getDatalayerPage(
    page: Page,
    config: WebCommonConfig,
    urlTree: UrlTree,
    category: Category
  ): Record<string, any> {
    if (page) {
      return {
        page_type: DatalayerService.mapPageNameToPageType(page, urlTree, category),
        site_error: config.analytics?.error[page.name]
      };
    }
    if (urlTree?.root?.children?.['primary']?.segments?.[0]?.path === 'error') {
      return {
        page_type: 'errorPage',
        site_error: urlTree.root.children['primary'].segments[1].path
      };
    }
    return {};
  }

  private static mapPageNameToPageType(page: Page, urlTree: UrlTree, category: CustomCategory) {
    const pageLabel = page?.label?.startsWith('/') ? page.label : page?.label?.length > 0 ? `/${page?.label || ''}` : '';
    switch (pageLabel) {
      case '/':
        return 'homepage';
      case '/accessory/home':
        return 'accessories.home';
      case '/not-found':
        return 'error404_page';
      case '/search':
        if (urlTree?.queryParams?.target === 'accessoryProduct') {
          return 'accessories.search';
        }
        if (urlTree?.queryParams?.target === 'recipes') {
          return 'recipes.search';
        }
        if (urlTree?.queryParams?.target === 'faq') {
          return 'customer.faq.search';
        }
        if (urlTree?.queryParams?.target === 'ifu') {
          return 'customer.notices.search';
        }
        return 'product.search';
      case '/consumer-services/contact-us':
        return 'customer.contact-us';
      case '/consumer-services/warranty-and-repairs/repairers':
        return 'customer.repairers.search';
      case '/warranty-&-repair':
        return 'customer.warranty';
      case '/forfait-reparation':
        return 'customer.repair-package';
      case '/extension-garantie':
        return 'customer.warranty-extension';
      case '/faq-home':
        return 'customer.faq.home';
      case '/faq-general':
        return 'customer.faq.general';
      case '/faq-forfait':
        return 'customer.faq.repair-service';
      case '/faq-extensions-de-garantie':
        return 'customer.faq.warranty-extension';
      case '/faq/csc/products':
        if (category?.subCategories?.length > 0) {
          return 'customer.faq.list_level1';
        }
        if (category) {
          return 'customer.faq.list_level2';
        }
        return 'customer.faq.list_entry';
      case '/instructions-for-use':
        if (category?.subCategories?.length > 0) {
          return 'customer.notices.list_level1';
        }
        if (category) {
          return 'customer.notices.list_level2';
        }
        return 'customer.notices.list_entry';
      case '/cart':
        return 'checkout.basket';
      case '/checkout/multi/choose-delivery':
        return 'checkout.delivery';
      case '/checkout/multi/choose-payment-method':
        return 'checkout.payment';
      case '/checkout/express/review':
        return 'checkout.express.review';
      case '/order-confirmation':
        return 'checkout.confirmation';
      case '/recipe':
        return 'recipes.home';
      case '/recipe/detail':
        return 'recipes.detail';
      case '/my-account-dashboard':
        return 'account.dashboard';
      case '/my-account':
        return 'account.information';
      case '/my-orders':
        return 'account.orders';
      case '/my-newsletters':
        return 'account.newsletter';
      case '/my-products':
        return 'account.products';
      case '/recipe/list':
        return 'recipes.list';
      case '/terms-of-use':
        return 'sales_conditions';
      case '/refer-a-friend':
        return 'refer-a-friend';
      default:
        if (page.template.includes('ProductDetailsPageTemplate')) {
          const pdpType = getPdpType(urlTree?.root?.children?.primary?.segments?.[0]?.path);
          if (pdpType === PdpTypeEnum.PRODUCT) {
            return 'products.detail';
          }
          if (pdpType === PdpTypeEnum.ACCESSORY) {
            if (urlTree?.root?.children?.primary?.segments?.find((segment) => segment.path === 'csp')) {
              return 'accessories.accessory_detail';
            }
            return 'accessories.detail';
          }
          if (pdpType === PdpTypeEnum.IFU) {
            return 'customer.notices.detail';
          }
          if (pdpType === PdpTypeEnum.FAQ) {
            return 'customer.faq.detail';
          }
          return '';
        }
        if (page.template.includes('SubCategoryPageTemplate')) {
          if (urlTree?.root?.children?.primary?.segments?.find((segment) => segment.path === 'csc')) {
            return 'accessories.list.level2';
          }
          return 'products.list';
        }
        if (page.template.includes('CategoryPageTemplate')) {
          if (urlTree?.root?.children?.primary?.segments?.find((segment) => segment.path === 'csc')) {
            return 'accessories.list.level1';
          }
          return 'products.entry';
        }
        if (page.template.includes('NewGenerationTemplate')) {
          return 'other.free_content_page';
        }
        return '';
    }
  }

  private static sortByPosition(script1: TMSScript, script2: TMSScript) {
    return DatalayerService.getPositionValue(script1.position) - DatalayerService.getPositionValue(script2.position);
  }

  private static getPositionValue(position: TMSScriptPositionEnum): number {
    switch (position) {
      case TMSScriptPositionEnum.TMS_HEADER:
        return 0;
      case TMSScriptPositionEnum.TAG_HEADER:
        return 1;
      case TMSScriptPositionEnum.TAG_TOP_BODY:
        return 2;
      case TMSScriptPositionEnum.TAG_BOTTOM_BODY:
        return 3;
    }
  }

  private static getPositionNode(position: TMSScriptPositionEnum): ScriptPlacement {
    switch (position) {
      case TMSScriptPositionEnum.TMS_HEADER:
      case TMSScriptPositionEnum.TAG_HEADER:
        return ScriptPlacement.HEAD;
      case TMSScriptPositionEnum.TAG_TOP_BODY:
      case TMSScriptPositionEnum.TAG_BOTTOM_BODY:
        return ScriptPlacement.BODY;
    }
  }

  private static getDataLayerSearch(searchQuery: string, searchType: string, searchTotal: number): DataLayerSearch {
    return {
      search_query: searchQuery ?? '',
      search_type: searchType ?? '',
      search_results_count: searchTotal ?? 0
    };
  }

  init(): void {
    if (isPlatformBrowser(this.platformId) && !this.isDisabled) {
      this.configService.getConfig().subscribe(
        (configs) => this.tagCommanderService.setDebug(configs.debug)
      );
      this.tagCommanderService.setTcVar('internal_promotion', []);

      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          distinctUntilChanged((prev: NavigationEnd, curr: NavigationEnd) => {
            const prevParsedUrl = this.router.parseUrl(prev.url);
            const currParsedUrl = this.router.parseUrl(curr.url);
            return (
              areSegmentsEqual(prevParsedUrl, currParsedUrl) &&
              areTargetsEqual(prevParsedUrl, currParsedUrl) &&
              areQueryEqual(prevParsedUrl, currParsedUrl) &&
              areCurrentPageEqual(prevParsedUrl, currParsedUrl)
            );
          }),
          takeUntil(this.destroy$)
        )
        .subscribe((event: NavigationEnd) => {
          this.navigation = event;
          this.lastSubjectId = uuid();

          this.loadTMSScripts(event);
        });
    }
  }

  updateDataLayer() {
    if (this.isDisabled) {
      return;
    }
    this.getDatalayer(this.navigation).subscribe((datalayer) => this.tagCommanderService.setTcVars(datalayer));
  }

  isScriptLoaded(): Observable<boolean> {
    return this.isScriptLoaded$.asObservable();
  }

  setProductList(products: Product[], pushCount: number): void {
    this.productList$.next({ id: this.lastSubjectId, products, pushCount });
  }

  setSearchData(searchQuery: string, searchType: string, searchTotal: number): void {
    this.searchData$.next({ id: this.lastSubjectId, searchQuery, searchType, searchTotal });
  }

  setDataLayerInternalPromotion(title?: string, code?: string, position?: string, bannerType?: string): void {
    if (this.isDisabled) {
      return;
    }

    const updatedPromotions = this.tagCommanderService.getTcVar('internal_promotion');
    updatedPromotions.push({
      promotion_id: code ?? '',
      promotion_name: title ?? '',
      promotion_position: position ?? '',
      promotion_type: bannerType ?? ''
    });
    this.tagCommanderService.setTcVar('internal_promotion', updatedPromotions);
  }

  getBundleLabel(bundle: Bundle): string[] {
    const labels = [];
    if (bundle.discountedPrice?.value !== bundle.price?.value) {
      labels.push(this.translateService.instant('bdp.tag.promotion'));
    }
    if (bundle.products?.length > 0) {
      const bundleMainProduct = bundle.products[0];
      if (bundleMainProduct?.bestSeller) {
        labels.push(this.translateService.instant('bdp.tag.bestSeller'));
      }
      if (bundleMainProduct?.promoMessageEnabled) {
        const text = bundleMainProduct?.promoMessage?.split('<promotion>', 2)[1]?.split('</promotion>', 2)[0];
        if (text) {
          labels.push(text);
        }
      }
    }
    return labels.length > 0 ? labels : [];
  }

  getProductLabel(product: Product): string[] {
    const labels = [];
    if (product?.bestSeller) {
      labels.push(this.translateService.instant('product.tags.bestSeller'));
    }
    if (product?.newProduct) {
      labels.push(this.translateService.instant('product.tags.newProduct'));
    }
    if (product?.promoMessageEnabled) {
      const text = product?.promoMessage?.split('<promotion>', 2)[1]?.split('</promotion>', 2)[0];
      if (text) {
        labels.push(text);
      }
    }
    return labels.length > 0 ? labels : [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTMSScripts(navigation: NavigationEnd) {
    this.isScriptLoaded$.next(false);
    this.datalayerSubscription?.unsubscribe();
    this.datalayerSubscription = this.getTMSScripts()
      .pipe(
        filter((scripts) => scripts.length > 0),
        switchMap((scripts) => forkJoin([of(scripts), this.getDatalayer(navigation)]))
      )
      .subscribe(([scripts, datalayer]) => {
        this.tcContainers.forEach((tcScript) => {
          if (tcScript.executionType === TmsScriptExecutionTypeEnum.EVERY_PAGE_CHANGE) {
            this.document.getElementById(tcScript.code)?.remove();
          }
        });
        this.tcContainers = [];
        this.document.querySelectorAll('script[id^=tc_script]').forEach((element) => element.remove());

        this.tagCommanderService.setTcVars(datalayer);

        this.loadNextScript(scripts);
      });
  }

  private loadNextScript(scripts: TMSScript[]) {
    if (scripts.length > 0) {
      const script = scripts.shift();
      if (script.executionType === TmsScriptExecutionTypeEnum.ONE_BY_SESSION) {
        if (this.document.getElementById(script.code)) {
          this.tcContainers.push({
            code: script.code,
            executionType: script.executionType
          });
          this.loadNextScript(scripts);
          return;
        }
      }

      this.scriptLoader.embedScript({
        src: script.url,
        attributes: { id: script.code },
        placement: DatalayerService.getPositionNode(script.position),
        callback: () => this.loadNextScript(scripts),
        asyncLoad: script.scriptMode === this.tmsScriptModeEnum.ASYNC
      });
      this.tcContainers.push({
        code: script.code,
        executionType: script.executionType
      });
    } else {
      this.isScriptLoaded$.next(true);
    }
  }

  private getTMSScripts(): Observable<TMSScript[]> {
    const endpoint = this.occEndpointsService.buildUrl('tmsScripts');
    return this.http.get<{ tmsScripts: TMSScript[] }>(endpoint).pipe(
      map(({ tmsScripts }) => tmsScripts),
      tap((tmsScripts: TMSScript[]) => tmsScripts.sort(DatalayerService.sortByPosition))
    );
  }

  private getDatalayer(navigation: NavigationEnd): Observable<Record<string, any>> {
    const baseStore$ = this.baseStoreService.getBaseStore().pipe(
      map(({ market, brandSite }) => {
        return [StoreMarketEnum[market], brandSite];
      })
    );

    const liveBrand$ = this.hobStateService.hobState$.pipe(
      map((state) => state.liveBrand ?? '')
    );

    const config$ = this.configService.getConfig();
    const siteId$ = this.baseSiteService.getActive().pipe(filter((id) => !!id));
    const page$ = this.cmsService.getCurrentPage();
    const language$ = this.languageService.getActive().pipe(filter((lang) => !!lang));
    const currency$ = this.currencyService.getActive().pipe(filter((currency) => !!currency));

    const cart$ = this.activeCartService.getLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.activeCartService.getActive()),
      switchMap((cart) => this.updateEntryGroupsAfterExtendedWarrantyCheck(cart))
    );

    const userInfo$ = this.authService.isUserLoggedIn().pipe(
      switchMap((isUserLoggedIn) => (isUserLoggedIn
        ? this.userAccountFacade.get().pipe(first((user)=>!!user))
        : of(undefined))
      )
    );

    const category$ = this.categoryService.getCurrentCategory().pipe(
      filter((cat) => cat !== undefined)
    );

    const parsedUrl = this.router.parseUrl(navigation.url);
    let product$: Observable<Product>;
    let bundle$: Observable<Bundle>;

    if (parsedUrl.queryParamMap.has('bundle')) {
      product$ = of(undefined);
      bundle$ = this.bundleService.getBundle(parsedUrl.queryParamMap.get('bundle'));
    } else {
      product$ = this.currentProductService.getProduct();
      bundle$ = of(undefined);
    }

    const orderCode = this.activatedRoute.snapshot.firstChild?.paramMap?.get('orderCode');

    const order$ = orderCode
      ? this.orderService.getOrderDetails(orderCode).pipe(
        switchMap((order) => this.updateEntryGroupsAfterExtendedWarrantyCheck(order))
      )
      : of(undefined);

    const currentProductList$: Observable<ProductList> = this.cmsService.getCurrentPage().pipe(
      filter((p) => !!p),
      map(({ template }) => (
        (template.includes('SubCategoryPageTemplate')) || (template.includes('SearchResultPageTemplate') && (
          !parsedUrl.queryParamMap.has('target') || TARGET_LIST.includes(parsedUrl.queryParamMap.get('target'))
        ))
      )),
      switchMap((hasProductList) => hasProductList ? this.productList$.asObservable() : of({} as ProductList)),
      filter((productList) => (
        !!productList && (!productList.id || productList.id === this.lastSubjectId)
      ))
    );

    const currentSearchData$: Observable<any> = this.cmsService.getCurrentPage().pipe(
      filter((p) => !!p),
      switchMap((currentPage) =>
        currentPage.template.includes('SearchResultPageTemplate') ? this.searchData$.asObservable() : of({})
      ),
      filter((searchData: { id: string }) => !!searchData && (!searchData.id || searchData.id === this.lastSubjectId))
    );

    return combineLatest([
      config$,
      siteId$,
      baseStore$,
      liveBrand$,
      page$,
      language$,
      currency$,
      cart$,
      userInfo$,
      category$,
      product$,
      bundle$,
      order$,
      currentProductList$,
      currentSearchData$
    ]).pipe(
      take(1),
      map(([
        config, siteId, [country, siteBrand], liveBrand, page, language, currency, cart, userInfo,
        category, product, bundle, order, productList, searchData
      ]: [
        WebCommonConfig, string, string[], BrandsEnum, Page, string, string, Cart, Profil,
        Category, Product, Bundle, CustomOrder, ProductList, DataLayerSearch
      ]) => ({
        site_id: siteId,
        site_env: config.analytics?.siteEnv,
        site_brand: liveBrand || this.hobMapper.currentBrandToBrandEnum(siteBrand),
        site_country: country,
        site_lang: language,
        cart_currency: currency,
        user_device: this.deviceDetectorService.isMobile()
          ? 'mobile'
          : this.deviceDetectorService.isTablet()
            ? 'tablet'
            : 'desktop',
        ...DatalayerService.getDatalayerCategory(category),
        ...DatalayerService.getDatalayerPage(page, config, parsedUrl, category),
        ...this.getDatalayerUser(cart, userInfo, order),
        ...(bundle ? this.getDataLayerBundle(bundle) : product ? this.getDataLayerProduct(product) : null),
        ...this.getDataLayerCart(cart),
        ...this.getDatalayerTransaction(order),
        ...this.getDataLayerProductList(productList.products, productList.pushCount),
        ...DatalayerService.getDataLayerSearch(
          searchData ? searchData['search_query'] : '',
          searchData ? searchData['search_type'] : '',
          searchData ? searchData['search_results_count'] : 0
        )
      }))
    );
  }

  private getDatalayerUser(cart: Cart, userInfo: Profil, order: CustomOrder) {
    if (userInfo) {
      return {
        user_status: 'Logged in',
        user_id: userInfo.rcuAccountId,
        user_has_transacted: userInfo.nbrOrders > 0 ? 'yes' : 'no',
        user_transaction_count: userInfo.nbrOrdersInProgress,
        user_encryptedEmail: userInfo.encryptedEmail
      };
    } else {
      return {
        user_status: 'Guest',
        user_id: (cart?.user?.name?.toLowerCase() === 'anonymous' || cart?.user?.name?.toLowerCase() === 'guest')
        && cart?.user?.uid && cart?.user?.uid !== 'anonymous'
          ? cart?.user?.uid.split('|')[0]
          : (order?.user?.name?.toLowerCase() === 'anonymous' || order?.user?.name?.toLowerCase() === 'guest')
          && order?.user?.uid && order?.user?.uid !== 'anonymous'
            ? order?.user?.uid.split('|')[0]
            : undefined,
        user_has_transacted: 'no',
        user_transaction_count: '',
        user_encryptedEmail: cart?.user?.encryptedEmail ? cart?.user?.encryptedEmail : order?.user?.encryptedEmail
      };
    }
  }

  private getDataLayerBundle(bundle: Bundle): DataLayerProductOrBundle {
    const bundleMainProduct = bundle.products.length > 0 ? bundle.products[0] : undefined;
    let dataLayerBundle: DataLayerProductOrBundle;
    if (bundle) {
      dataLayerBundle = {
        product_id: bundle.id ? bundle.id : '',
        product_name: bundle.name ? bundle.name : '',
        product_availability: !bundleMainProduct?.onSale
          ? StockAvailabilityEnum.NOT_ON_SALE
          : bundle.stock > 0
            ? StockAvailabilityEnum.IN_STOCK
            : StockAvailabilityEnum.NO_STOCK,
        product_label: this.getBundleLabel(bundle),
        marketing_category: bundleMainProduct?.topCategoryName ? bundleMainProduct?.topCategoryName : '',
        marketing_subcategory: bundleMainProduct?.subCategoryName ? bundleMainProduct?.subCategoryName : '',
        SIS_category: bundleMainProduct?.sisCategory ? bundleMainProduct?.sisCategory : '',
        SIS_subcategory: bundleMainProduct?.sisSubCategory ? bundleMainProduct?.sisSubCategory : '',
        product_type: ProductTypeEnum.BUNDLE,
        product_price: bundle.discountedPrice?.value ?? bundle.price?.value ?? 0,
        product_currency: bundle.price?.currencyIso ? bundle.price?.currencyIso : '',
        product_brand: bundle.brand ? bundle.brand : BrandsEnum.default
      };
    } else {
      dataLayerBundle = {
        product_id: '',
        product_name: '',
        product_availability: '',
        product_label: [],
        marketing_category: '',
        marketing_subcategory: '',
        SIS_category: '',
        SIS_subcategory: '',
        product_type: ProductTypeEnum.EMPTY,
        product_price: 0,
        product_currency: ''
      };
    }
    return dataLayerBundle;
  }

  private updateEntryGroupsAfterExtendedWarrantyCheck(obj) {
    if (!obj.entryGroups?.length) {
      return of(obj);
    }

    const objEntriesGroups$ = obj.entryGroups.map((entryGroup) => {
      const objEntries$ = entryGroup.entries.map((entry) =>
        this.extendedWarrantyService.checkProductEligibilityForExtendedWarranty(entry.product).pipe(
          take(1),
          catchError(() => of([])),
          map((isEligible) => ({
            ...entry,
            product_warranty: isEligible ? (entry.extendedWarrantyOffer ? 'yes' : 'no') : 'not eligible'
          }))
        )
      );

      return combineLatest(objEntries$).pipe(
        take(1),
        map((entriesUpdated) => ({
          ...entryGroup,
          entries: entriesUpdated
        }))
      );
    });

    return combineLatest(objEntriesGroups$).pipe(
      take(1),
      map((entriesGroupsUpdated) => ({
        ...obj,
        entryGroups: entriesGroupsUpdated
      }))
    );
  }

  private getDataLayerProduct(product: Product): DataLayerProductOrBundle {
    let dataLayerProduct: DataLayerProductOrBundle;
    if (product) {
      dataLayerProduct = {
        product_id: product.code ? product.code : '',
        product_name: product.name ? product.name : '',
        product_availability: !product?.onSale
          ? StockAvailabilityEnum.NOT_ON_SALE
          : product.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK
            ? StockAvailabilityEnum.LOW_STOCK
            : product.stock?.stockLevelStatus === StockStatusEnum.OUT_OF_STOCK
              ? StockAvailabilityEnum.NO_STOCK
              : StockAvailabilityEnum.IN_STOCK,
        product_label: this.getProductLabel(product),
        marketing_category: product.topCategoryName ? product.topCategoryName : '',
        marketing_subcategory: product.subCategoryName ? product.subCategoryName : '',
        SIS_category: product.sisCategory ? product.sisCategory : '',
        SIS_subcategory: product.sisSubCategory ? product.sisSubCategory : '',
        product_brand: product.brand ? product.brand : BrandsEnum.default,
        product_type: product.isAccessory
          ? product.productGenre === 'Forfait'
            ? ProductTypeEnum.REPAIR_PACKAGE
            : ProductTypeEnum.ACCESSORIES
          : ProductTypeEnum.PRODUCTS
      };
      if (product.price) {
        dataLayerProduct.product_price = product.discountedPrice?.value ?? product.price.value;
        dataLayerProduct.product_currency = product.price.currencyIso;
      } else {
        dataLayerProduct.product_price = 0;
        dataLayerProduct.product_currency = '';
      }
    } else {
      dataLayerProduct = {
        product_id: '',
        product_name: '',
        product_availability: '',
        product_label: [],
        marketing_category: '',
        marketing_subcategory: '',
        SIS_category: '',
        SIS_subcategory: '',
        product_type: ProductTypeEnum.EMPTY,
        product_price: 0,
        product_currency: ''
      };
    }
    return dataLayerProduct;
  }

  private getDataLayerCart(cart: Cart): DataLayerCart {
    let dataLayerCart: DataLayerCart;
    if (cart) {
      dataLayerCart = {
        cart_id: cart.guid ? cart.guid : '',
        cart_subtotal: cart?.subTotalPriceWithProductDiscounts?.value
          ? cart?.subTotalPriceWithProductDiscounts?.value
          : 0,
        cart_total: cart.totalPrice?.value ? cart.totalPrice?.value : 0,
        cart_currency: cart.totalPrice?.currencyIso ? cart.totalPrice?.currencyIso : '',
        voucher_availability:
          cart.appliedOrderPromotions?.length > 0 || cart.appliedProductPromotions?.length > 0 ? 'yes' : '',
        voucher_operation: getVoucherOperation(cart) ?? '',
        voucher_code: getVoucherCode(cart) ?? '',
        product_voucher_code: getProductVoucherCode(cart) ?? '',
        product_discounts: getProductVoucherDiscount(cart) ?? '',
        voucher_discount: getVoucherDiscount(cart) ?? '',
        unique_id: cart.code ? cart.code : ''
      };

      const cartTotalItems = [];
      const cartStandaloneEntries = findStandaloneEntries(cart);
      const cartBundleEntries = findBundleEntries(cart);

      if (cartStandaloneEntries?.length > 0) {
        cartTotalItems.push(...this.getDatalayerCartGroupEntriesProducts(cartStandaloneEntries, cart));
      }
      if (cartBundleEntries?.length > 0) {
        cartTotalItems.push(...this.getDatalayerCartGroupEntriesBundles(cartBundleEntries));
      }
      dataLayerCart['cart_items'] = cartTotalItems;
    } else {
      dataLayerCart = {
        cart_id: '',
        cart_subtotal: 0,
        cart_total: 0,
        cart_currency: '',
        voucher_availability: '',
        voucher_operation: '',
        voucher_code: '',
        product_voucher_code: '',
        voucher_discount: '',
        unique_id: ''
      };
    }
    return dataLayerCart;
  }

  private getDatalayerCartGroupEntriesProducts(
    transactions: CartGroupEntry[],
    orderOrCart?: CustomOrder | Cart
  ): DataLayerProductOrBundle[] {
    const products: DataLayerProductOrBundle[] = [];
    if (orderOrCart) {
      transactions.forEach((productEntry) => {
        productEntry.entries?.forEach((entry: CustomOrderEntry) => {
          const dataLayerProduct = this.getDataLayerProduct(entry.product);
          const orderEntryNumberPromo = orderOrCart.appliedProductPromotions?.[0]?.['consumedEntries']?.[0]?.orderEntryNumber;
          const isInPromo = orderEntryNumberPromo === entry.entryNumber;
          products.push({
            ...dataLayerProduct,
            quantity: entry.quantity ? entry.quantity : 0,
            product_total: entry.totalPrice?.value ? entry.totalPrice?.value : 0,
            product_discounts: isInPromo ? getEntryDiscountForProduct(orderOrCart) : 0,
            product_voucher_code: isInPromo ? getProductVoucherCode(orderOrCart) : '',
            product_warranty: entry?.product_warranty
          });
        });
      });
      return products;
    } else {
      transactions.forEach((productEntry) => {
        productEntry.entries?.forEach((entry: CustomOrderEntry) => {
          const dataLayerProduct = this.getDataLayerProduct(entry.product);
          products.push({
            ...dataLayerProduct,
            quantity: entry.quantity ? entry.quantity : 0,
            product_warranty: entry?.product_warranty
          });
        });
      });
      return products;
    }
  }

  private getDatalayerCartGroupEntriesBundles(
    transactions: CartGroupEntry[],
    order?: CustomOrder
  ): DataLayerProductOrBundle[] {
    const bundles: DataLayerProductOrBundle[] = [];
    if (order) {
      transactions.forEach((bundleEntry) => {
        bundles.push(this.getDatalayerBundleFromEntry(bundleEntry, order));
      });
      return bundles;
    } else {
      transactions.forEach((bundleEntry) => {
        bundles.push(this.getDatalayerBundleFromEntry(bundleEntry));
      });
      return bundles;
    }
  }

  private getDatalayerBundleFromEntry(productEntry: CartGroupEntry, order?: CustomOrder): DataLayerProductOrBundle {
    const bundleMainProduct: Product = productEntry.entries?.[0]?.product
      ? productEntry.entries?.[0]?.product
      : undefined;
    let dataLayerBundle: DataLayerProductOrBundle = {};
    const customOrderEntry: CustomOrderEntry = productEntry?.entries[0];
    if (!!productEntry && bundleMainProduct) {
      dataLayerBundle = {
        product_id: productEntry.externalReferenceId ? productEntry.externalReferenceId : '',
        product_name: productEntry.label ? productEntry.label : '',
        product_label: this.getProductLabel(bundleMainProduct),
        marketing_category: bundleMainProduct?.topCategoryName ? bundleMainProduct?.topCategoryName : '',
        marketing_subcategory: bundleMainProduct?.subCategoryName ? bundleMainProduct?.subCategoryName : '',
        SIS_category: bundleMainProduct?.sisCategory ? bundleMainProduct?.sisCategory : '',
        SIS_subcategory: bundleMainProduct?.sisSubCategory ? bundleMainProduct?.sisSubCategory : '',
        product_type: ProductTypeEnum.BUNDLE,
        product_price: productEntry.discountedPrice?.value ? productEntry.discountedPrice?.value : productEntry.price,
        product_currency: productEntry.totalPrice?.currencyIso ? productEntry.totalPrice?.currencyIso : '',
        quantity: productEntry.selectedQuantity ? productEntry.selectedQuantity : 1
      };
      if (order) {
        const orderEntryNumberPromo = order.appliedProductPromotions?.[0]?.['consumedEntries']?.[0]?.orderEntryNumber;
        const isInPromo = orderEntryNumberPromo === customOrderEntry.entryNumber;
        dataLayerBundle['product_voucher_code'] = isInPromo ? getProductVoucherCode(order) ?? '' : '';
        dataLayerBundle['product_total'] = productEntry.totalPrice?.value ? productEntry.totalPrice?.value : 0;
        dataLayerBundle['product_discounts'] = getProductEntryDiscount(customOrderEntry) ?? 0;
      }
    }
    return dataLayerBundle;
  }

  private getDatalayerTransaction(order: CustomOrder): DataLayerTransaction {
    let dataLayerTransaction: DataLayerTransaction;
    if (order) {
      dataLayerTransaction = {
        order_id: order.code ? order.code : '',
        order_currency: order.totalPrice?.currencyIso ? order.totalPrice?.currencyIso : '',
        order_payment_method: order.selectedPaymentMode ? order.selectedPaymentMode : '',
        order_subtotal: order?.subTotalPriceWithProductDiscounts?.value
          ? order?.subTotalPriceWithProductDiscounts?.value
          : 0,
        order_tax: order.totalTax?.value ? order.totalTax?.value : 0,
        voucher_operation: getVoucherOperation(order) ?? '',
        voucher_code: getVoucherCode(order) ?? '',
        product_voucher_code: getProductVoucherCode(order) ?? '',
        voucher_discount: getVoucherDiscount(order) ?? '',
        shipping_cost:
          (order.deliveryCost?.value ? order.deliveryCost?.value : 0) +
          (order.paymentCost?.value ? order.paymentCost?.value : 0),
        shipping_method: order.deliveryMode?.code,
        order_total: order.totalPrice?.value ? order.totalPrice?.value : 0,
        unique_id: order.cartId ? order.cartId : ''
      };

      const transactionTotal = [];
      const transactionStandaloneEntries = findStandaloneEntries(order);
      const transactionBundleEntries = findBundleEntries(order);

      if (transactionStandaloneEntries?.length > 0) {
        transactionTotal.push(...this.getDatalayerCartGroupEntriesProducts(transactionStandaloneEntries, order));
      }
      if (transactionBundleEntries?.length > 0) {
        transactionTotal.push(...this.getDatalayerCartGroupEntriesBundles(transactionBundleEntries));
      }
      dataLayerTransaction.order_items = transactionTotal;
    } else {
      dataLayerTransaction = {
        order_id: '',
        order_currency: '',
        order_payment_method: '',
        order_subtotal: 0,
        order_tax: 0,
        shipping_cost: 0,
        shipping_method: '',
        order_total: 0,
        order_items: []
      };
    }
    return dataLayerTransaction;
  }

  private getDataLayerProductList(products: Product[], pushCount = 0): Record<string, any> {
    let indexCounter = 0;
    const dataLayerProductList: DataLayerProductOrBundle[] = [];
    products?.forEach((product: Product) => {
      indexCounter++;
      if (product) {
        const dataLayerProduct = this.getDataLayerProduct(product);
        if (dataLayerProductList.length < 9 - pushCount) {
          dataLayerProductList.push({
            position: indexCounter,
            ...dataLayerProduct
          });
        }
        product.indexedBundles?.forEach((indexedBundle: Bundle) => {
          indexCounter++;
          const dataLayerBundle = this.getDataLayerBundle(indexedBundle);
          if (dataLayerProductList.length < 9 - pushCount) {
            dataLayerProductList.push({
              position: indexCounter,
              ...dataLayerBundle
            });
          }
        });
      }
    });
    return { product_list: dataLayerProductList };
  }
}
